// Route: /shared/$token — публичная (auth: public) read-only страница расшаренного
// материала / набора. Минимальный shell (без MainLayout — см. __root.tsx).
//
// Реальный flow: GET /api/materials/shared/:token (raw Fastify route, public —
// apps/server/src/modules/materials/raw-routes/index.ts) отдаёт материал ИЛИ
// набор по shareToken. Эндпоинт ожидает токен 8..64 символов; ответ —
// discriminated union { kind: "material" | "set", data }.
//
// См. docs/ui-spec/shared.[token].{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { cn, NARROW_PAGE_SHELL, PAGE_CHROME_BAR } from "@studyhub/ui";
import { Button } from "@studyhub/ui/components/button";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { TintTile } from "@studyhub/ui/components/studyhub/tint-tile";
import { IconArrowRight, IconFolder, IconLock, IconSparkles } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { getSourceMeta } from "@/components/materials/_constants";
import { PageMeta } from "@/components/seo/PageMeta";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useLoginModalStore } from "@/stores/login-modal";

// API base: абсолютный VITE_API_URL (Capacitor) либо same-origin "" (web).
const API_BASE = import.meta.env.VITE_API_URL ?? "";

// MarkdownLatex тянет KaTeX CSS — грузим только когда есть контент для рендера.
const MarkdownLatex = lazy(() => import("@/components/visual-explainer/markdown-latex"));

const searchSchema = z.object({
	demo: z.enum(["default", "loading", "empty", "error"]).optional(),
});

// Контракт ответа raw-route GET /api/materials/shared/:token.
const sharedMaterialSchema = z.object({
	kind: z.literal("material"),
	data: z.object({
		id: z.string(),
		name: z.string(),
		type: z.string(),
		mimeType: z.string().nullable(),
		fileSizeBytes: z.number(),
		duration: z.number().nullable(),
		content: z.string().nullable(),
		transcription: z.string().nullable(),
		youtubeThumbnail: z.string().nullable(),
		thumbHashBase64: z.string().nullable(),
		createdAt: z.string(),
		updatedAt: z.string(),
	}),
});

const sharedSetSchema = z.object({
	kind: z.literal("set"),
	data: z.object({
		id: z.string(),
		name: z.string(),
		description: z.string().nullable(),
		color: z.string().nullable(),
		materialsCount: z.number(),
		materials: z.array(
			z.object({
				id: z.string(),
				name: z.string(),
				type: z.string(),
				fileSizeBytes: z.number(),
				thumbnailUrl: z.string().nullable(),
				thumbHashBase64: z.string().nullable(),
				youtubeThumbnail: z.string().nullable(),
			}),
		),
	}),
});

const sharedResponseSchema = z.discriminatedUnion("kind", [sharedMaterialSchema, sharedSetSchema]);

type SharedResponse = z.infer<typeof sharedResponseSchema>;

export const Route = createFileRoute("/shared/$token")({
	validateSearch: tolerantSearch(searchSchema),
	component: SharedPage,
	pendingComponent: PagePendingFallback,
});

async function fetchShared(token: string): Promise<SharedResponse | null> {
	const resp = await fetch(`${API_BASE}/api/materials/shared/${encodeURIComponent(token)}`, {
		headers: { accept: "application/json" },
	});
	if (resp.status === 404) return null;
	if (!resp.ok) throw new Error(`Failed to load shared material: ${resp.status}`);
	return sharedResponseSchema.parse(await resp.json());
}

function SharedShell({ children }: { children: React.ReactNode }): React.ReactElement {
	const { t } = useTranslation("materials");
	return (
		<div className="min-h-dvh flex flex-col bg-background">
			{/* Overlay без Topbar — шапка сама красит notch/status bar. */}
			<header className="flex h-[calc(3.5rem+env(safe-area-inset-top,0px))] items-center border-b border-border pt-[env(safe-area-inset-top,0px)]">
				<div className={cn(PAGE_CHROME_BAR, "max-w-form w-full")}>
					<Link to="/" className="flex items-center gap-2 text-lg font-bold cursor-pointer">
						<IconSparkles size={20} stroke={1.75} className="text-primary" /> StudyHub
					</Link>
					<Button
						size="sm"
						className="cursor-pointer gap-1.5 h-11 sm:h-8"
						onClick={() => useLoginModalStore.getState().open()}
					>
						{t("share.signupCta")} <IconArrowRight size={14} stroke={1.75} />
					</Button>
				</div>
			</header>
			<main className={cn(NARROW_PAGE_SHELL, "flex-1 space-y-section")}>{children}</main>
			<footer className="border-t border-border py-4 text-center text-xs text-muted-foreground">
				StudyHub · {t("share.watermark")}
			</footer>
		</div>
	);
}

function SharedPage(): React.ReactElement {
	const { t } = useTranslation("materials");
	const { token } = useParams({ from: "/shared/$token" });
	const search = Route.useSearch();

	const query = useQuery({
		queryKey: ["shared-material", token],
		queryFn: () => fetchShared(token),
		retry: false,
		staleTime: 60_000,
	});

	const isLoading = search.demo === "loading" || query.isLoading;
	const isError = search.demo === "error" || query.isError;
	const result = search.demo === "empty" ? null : (query.data ?? null);
	const isUnavailable = !isLoading && !isError && result === null;

	if (isLoading) {
		return (
			<SharedShell>
				<div className="space-y-4">
					<Skeleton className="h-8 w-2/3" />
					<Skeleton className="h-4 w-1/3" />
					<Skeleton className="h-[40dvh] w-full" />
				</div>
			</SharedShell>
		);
	}

	if (isError || isUnavailable) {
		return (
			<SharedShell>
				<div className="rounded-xl border border-border bg-card p-8 text-center space-y-3">
					<TintTile
						tint="warning"
						size="lg"
						icon={<IconLock size={22} stroke={1.75} />}
						className="mx-auto"
					/>
					<h2 className="text-lg font-semibold">{t("share.notFoundTitle")}</h2>
					<p className="text-sm text-muted-foreground">{t("share.notFoundDescription")}</p>
				</div>
			</SharedShell>
		);
	}

	// result гарантированно не null — отфильтровано выше.
	const shared = result as SharedResponse;
	const sharedTitle =
		shared.kind === "material" ? shared.data.name : (shared.data.name ?? t("share.publicHeading"));

	return (
		<SharedShell>
			<PageMeta
				title={sharedTitle}
				description={t("share.publicSubheading")}
				path={`/shared/${token}`}
				robots="noindex, follow"
			/>
			{shared.kind === "material" ? (
				<SharedMaterialView material={shared.data} />
			) : (
				<SharedSetView set={shared.data} />
			)}

			{/* CTA-баннер */}
			<div className="rounded-xl border border-primary/20 bg-primary/5 p-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
				<TintTile tint="indigo" size="lg" icon={<IconSparkles size={22} stroke={1.75} />} />
				<div className="min-w-0 flex-1">
					<p className="text-md font-medium">{t("share.publicHeading")}</p>
					<p className="text-sm text-muted-foreground mt-0.5">{t("share.publicSubheading")}</p>
				</div>
				<Button
					className="cursor-pointer gap-1.5 w-full sm:w-auto sm:shrink-0"
					onClick={() => useLoginModalStore.getState().open()}
				>
					{t("share.signupCta")}
				</Button>
			</div>
		</SharedShell>
	);
}

function SharedMaterialView({
	material,
}: {
	material: z.infer<typeof sharedMaterialSchema>["data"];
}): React.ReactElement {
	const { t } = useTranslation("materials");
	const meta = getSourceMeta(material.type);
	const Icon = meta.icon;
	const body = material.content ?? material.transcription ?? "";

	return (
		<>
			<div className="space-y-2">
				<div className="text-xs text-muted-foreground tabular-nums">{t("share.watermark")}</div>
				<h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">{material.name}</h1>
				<p className="text-sm text-muted-foreground">
					{t(`upload.types.${material.type}`, material.type)}
				</p>
			</div>

			<article className="min-w-0 overflow-hidden rounded-xl border border-border bg-card p-6">
				<div className="flex items-center gap-2 mb-4">
					<TintTile tint={meta.tint} size="md" icon={<Icon size={18} stroke={1.75} />} />
					<span className="text-xs text-muted-foreground">{t("share.publicHeading")}</span>
				</div>
				{body.trim().length > 0 ? (
					<Suspense fallback={<Skeleton className="h-[40dvh] w-full" />}>
						<MarkdownLatex source={body} />
					</Suspense>
				) : (
					<p className="text-sm text-muted-foreground">{t("share.contentEmpty")}</p>
				)}
			</article>
		</>
	);
}

function SharedSetView({
	set,
}: {
	set: z.infer<typeof sharedSetSchema>["data"];
}): React.ReactElement {
	const { t } = useTranslation("materials");

	return (
		<>
			<div className="space-y-2">
				<div className="text-xs text-muted-foreground tabular-nums">{t("share.watermark")}</div>
				<h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">{set.name}</h1>
				<p className="text-sm text-muted-foreground tabular-nums">
					{t("share.setHeading")} · {t("share.setMaterialsCount", { count: set.materialsCount })}
				</p>
				{set.description && <p className="text-sm text-muted-foreground">{set.description}</p>}
			</div>

			<ul className="rounded-xl border border-border bg-card divide-y divide-border">
				{set.materials.map((m) => {
					const meta = getSourceMeta(m.type);
					const Icon = meta.icon;
					return (
						<li key={m.id} className="flex items-start gap-3 p-4">
							<TintTile tint={meta.tint} size="md" icon={<Icon size={18} stroke={1.75} />} />
							<div className="min-w-0 flex-1">
								{/* line-clamp-2, не truncate: имя материала — заголовок карточки,
								    на mobile важно видеть целиком (паттерн [B]) */}
								<p className="text-sm font-medium line-clamp-2 break-words">{m.name}</p>
								<p className="text-xs text-muted-foreground">
									{t(`upload.types.${m.type}`, m.type)}
								</p>
							</div>
						</li>
					);
				})}
				{set.materials.length === 0 && (
					<li className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
						<TintTile tint="indigo" size="md" icon={<IconFolder size={18} stroke={1.75} />} />
						{t("share.contentEmpty")}
					</li>
				)}
			</ul>
		</>
	);
}
